import { useEffect } from "react";
import { useAuth } from "./AuthContext";
import { setApiAuthState } from "../services/apiClient";

/**
 * apiClient.js's interceptors run outside React and can't call hooks, so
 * this component (rendered once, near the root, inside AuthProvider) pushes
 * the latest auth values into that module on every relevant change. See
 * services/apiClient.js for how they're consumed.
 */
export function AuthBridge() {
  const {
    getAccessToken, deviceFingerprint, sessionType, stayLoggedIn, stepUpMfa, logout,
    reportAuthBlock, reauthenticate,
  } = useAuth();

  useEffect(() => {
    setApiAuthState({
      getAccessToken,
      deviceFingerprint,
      sessionType,
      stayLoggedIn,
      stepUpMfa,
      // Only a dead credential logs out. The other two record a blocking
      // state for App.jsx to explain, because a redirect to the sign-out
      // page cannot fix either of them and destroys the evidence (#166).
      onUnauthorized: logout,
      onNotProvisioned: (message) => reportAuthBlock("not_provisioned", message),
      onAccountSuspended: (message) => reportAuthBlock("suspended", message),
      onAuthMisconfigured: (message) => reportAuthBlock("misconfigured", message),
      // NOT onUnauthorized: the token is valid, so logging out and back in
      // via a live IdP cookie would return the same aged auth_time and
      // refuse again. reauthenticate forces the real prompt (#457).
      onReauthRequired: reauthenticate,
    });
  }, [
    getAccessToken, deviceFingerprint, sessionType, stayLoggedIn, stepUpMfa, logout,
    reportAuthBlock, reauthenticate,
  ]);

  return null;
}
